import { getJson } from '@/lib/api';

/**
 * MCP usage — who uses the bcecloud connector (mcp.bcecloud.com) from Claude
 * or ChatGPT. Typed fetchers over /api/v3/tool-calls, gated on view_analytics.
 */

/** Where the Analytics → MCP usage screen lives. */
export const MCP_USAGE_PATH = '/analytics/mcp';

/** One tool and how often a person called it in the last 30 days. */
export interface McpToolCount {
  readonly toolName: string;
  readonly calls: number;
}

/** One person who has used the MCP server, as GET /tool-calls/usage returns them. */
export interface McpUsageUser {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly lastUsedAt: string;
  readonly calls7d: number;
  readonly calls30d: number;
  readonly errors30d: number;
  /** Failed share of the last 30 days' calls, 0–1; null with no calls. */
  readonly errorRate30d: number | null;
  /** OAuth client ids (CIMD metadata URLs) they have called from. */
  readonly clients: readonly string[];
  readonly topTools: readonly McpToolCount[];
}

/** The admin MCP usage report. */
export interface McpUsageReport {
  readonly windowDays: { readonly recent: number; readonly month: number };
  readonly totals: {
    readonly users: number;
    readonly activeUsers7d: number;
    readonly calls7d: number;
    readonly calls30d: number;
    readonly errorRate30d: number | null;
  };
  readonly users: readonly McpUsageUser[];
}

/** Reads the per-person MCP usage report. */
export function readMcpUsage(signal?: AbortSignal): Promise<McpUsageReport> {
  return getJson<McpUsageReport>('/tool-calls/usage', undefined, signal);
}

/**
 * Known MCP clients by the host of their client id. CIMD clients use their
 * metadata URL as the OAuth client id, so the host says which product it is.
 */
const CLIENT_HOSTS: Readonly<Record<string, string>> = {
  'claude.ai': 'Claude',
  'claude.com': 'Claude',
  'chatgpt.com': 'ChatGPT',
  'openai.com': 'ChatGPT',
};

/**
 * A readable name for an OAuth client id: the product for a known CIMD host,
 * the host for an unknown metadata URL, and "Registered client" for an opaque
 * id from dynamic client registration.
 *
 * @param clientId - The OAuth client id the token was issued to.
 * @returns The label to show.
 */
export function clientLabel(clientId: string): string {
  let url: URL;
  try {
    url = new URL(clientId);
  } catch {
    return 'Registered client';
  }
  const host = url.hostname.replace(/^www\./, '');
  const product = CLIENT_HOSTS[host];
  if (product === undefined) return host;
  return product === 'Claude' && /claude-code/i.test(url.pathname) ? 'Claude Code' : product;
}
